/* eslint-disable no-restricted-globals */
import { showBanner, showMessage } from '../../helpers/MessageUtility';
import { apimCall } from '../../helpers/api';
import {
  GetUserDetailsError,
  SavingUserError,
  SuccessfulUserDeactivation,
  DeactivatingUserError,
  SuccessfulUserReactivation,
  ReactivatingUserError,
  GetUsersError
} from '../../helpers/jargon';
import { formatString } from '../../helpers/utility';
import { RootState } from '../../gf-types';
import { GoFormzServices } from '../../AppSettings';
import { analyticsTrack } from '../../helpers/AnalyticsUtility';

export const FETCH_USER_REQUEST = 'FETCH_USER_REQUEST';
export const FETCH_USER_FAILURE = 'FETCH_USER_FAILURE';
export const FETCH_USER_SUCCESS = 'FETCH_USER_SUCCESS';
export const FETCH_NEXT_USER_PAGE = 'FETCH_NEXT_USER_PAGE';

export const UPDATE_USER_REQUEST = 'UPDATE_USER_REQUEST';
export const UPDATE_USER_SUCCESS = 'UPDATE_USER_SUCCESS';
export const UPDATE_USER_FAILURE = 'UPDATE_USER_FAILURE';

export const FETCH_USER_DETAILS_REQUEST = 'FETCH_USER_DETAILS_REQUEST';
export const FETCH_USER_DETAILS_SUCCESS = 'FETCH_USER_DETAILS_SUCCESS';
export const FETCH_USER_DETAILS_FAILURE = 'FETCH_USER_DETAILS_FAILURE';

export const DEACTIVATE_USER_REQUEST = 'DEACTIVATE_USER_REQUEST';
export const DEACTIVATE_USER_SUCCESS = 'DEACTIVATE_USER_SUCCESS';
export const DEACTIVATE_USER_FAILURE = 'DEACTIVATE_USER_FAILURE';

export const REACTIVATE_USER_REQUEST = 'REACTIVATE_USER_REQUEST';
export const REACTIVATE_USER_SUCCESS = 'REACTIVATE_USER_SUCCESS';
export const REACTIVATE_USER_FAILURE = 'REACTIVATE_USER_FAILURE';

export const SET_PASSWORD_REQUEST = 'SET_PASSWORD_REQUEST';
export const SET_PASSWORD_SUCCESS = 'SET_PASSWORD_SUCCESS';
export const SET_PASSWORD_FAILURE = 'SET_PASSWORD_FAILURE';

export const REMOVE_CURRENT_GROUP = 'REMOVE_CURRENT_GROUP';
export const RE_ADD_GROUP = 'RE_ADD_GROUP';

export const LOGOUT_DEVICE_REQUEST = 'LOGOUT_DEVICE_REQUEST';
export const LOGOUT_DEVICE_FAILURE = 'LOGOUT_DEVICE_FAILURE';
export const LOGOUT_DEVICE_SUCCESS = 'LOGOUT_DEVICE_SUCCESS';

export const UPDATE_USER_INFO = 'UPDATE_USER_INFO';
export const UPDATE_SELECTED_GROUPS = 'UPDATE_SELECTED_GROUPS';

export function handleFriendlyErrors(error) {
  if (error.status === '400' || error.status === '409') {
    const jsonResponse = JSON.parse(error.responseText);
    if (jsonResponse) {
      return jsonResponse.error;
    }
  }
  return '';
}

export function handleAxiosErrors(error) {
  if (error.response.status === 400 || error.response.status === 409) {
    if (error.response.data && error.response.data.error) {
      return error.response.data.error;
    }
  }
  return '';
}

export function setPasswordRequest(userId, password) {
  return {
    type: SET_PASSWORD_REQUEST,
    meta: {
      remote: true
    },
    payload: {
      userId,
      password,
      error: '',
      isFetching: true
    }
  };
}

export function fetchUserDetailsRequest(userId) {
  return {
    type: FETCH_USER_DETAILS_REQUEST,
    meta: {
      remote: true
    },
    payload: {
      userId,
      isFetching: true,
      error: ''
    }
  };
}

export function fetchUserDetailsSuccess(payload) {
  return {
    type: FETCH_USER_DETAILS_SUCCESS,
    payload
  };
}

export function fetchUserDetailsFailure(error) {
  return {
    type: FETCH_USER_DETAILS_FAILURE,
    payload: {
      error: handleFriendlyErrors(error) !== '' ? handleFriendlyErrors(error) : GetUserDetailsError,
      isFetching: false
    }
  };
}

export function updateUserRequest(userId) {
  return {
    type: UPDATE_USER_REQUEST,
    meta: {
      remote: true
    },
    payload: {
      userId,
      isFetching: true,
      error: ''
    }
  };
}

export function updateUserSuccess(payload) {
  return {
    type: UPDATE_USER_SUCCESS,
    payload: {
      payload
    }
  };
}

export function updateUserFailure(error) {
  return {
    type: UPDATE_USER_FAILURE,
    payload: {
      error: handleFriendlyErrors(error) !== '' ? handleFriendlyErrors(error) : SavingUserError,
      isFetching: false
    }
  };
}

export function fetchUserRequest(groupId = undefined, page = 1, pageSize = 50, append, sortBy, sortDir) {
  return {
    type: FETCH_USER_REQUEST,
    meta: {
      remote: true
    },
    payload: {
      isFetching: true,
      error: '',
      page,
      pageSize,
      groupId,
      sortBy,
      sortDir
    },
    append
  };
}

export const fetchUsers =
  (groupId = undefined, page = 1, pageSize = 5, append, sortBy = '', sortDir = '') =>
  (dispatch) => {
    let orderByEnabled = null;
    if (sortBy === 'active' && sortDir === 'asc') {
      orderByEnabled = true;
    }
    if (sortBy === 'active' && sortDir === 'desc') {
      orderByEnabled = false;
    }
    const requestBody = {
      groupId,
      skip: (page - 1) * pageSize,
      take: pageSize,
      sortBy,
      sortDir,
      orderByEnabled
    };

    dispatch(fetchUserRequest(groupId, page, pageSize, append, sortBy, sortDir));

    return apimCall(GoFormzServices.AccountService, '/users/list', 'POST', requestBody)
      .then((response) => dispatch(fetchUserSuccess(response.data, append)))
      .catch(() => dispatch(fetchUserFailure()));
  };

export function fetchUserSuccess(payload, append) {
  return { type: FETCH_USER_SUCCESS, isFetching: false, payload, append };
}

export function fetchUserFailure() {
  return {
    type: FETCH_USER_FAILURE,
    payload: {
      error: GetUsersError,
      isFetching: false
    }
  };
}

export function deactivateUserRequest(userId, userName, inList = true) {
  return {
    type: DEACTIVATE_USER_REQUEST,
    meta: {
      remote: true
    },
    payload: {
      userId,
      inList,
      userName,
      error: ''
    }
  };
}

export const deactivateUser =
  (userId, userName, inList = true) =>
  (dispatch) => {
    dispatch(deactivateUserRequest(userId, userName, inList));
    return apimCall(GoFormzServices.AccountService, `/users/${userId}/deactivate`, 'POST')
      .then(() => {
        analyticsTrack('User Activation Toggled', {
          status: 'Deactivated'
        });
        dispatch(deactivateUserSuccess(userId));
        showBanner(formatString(SuccessfulUserDeactivation, userName));
      })
      .catch((error) => {
        showMessage(handleFriendlyErrors(error) !== '' ? handleFriendlyErrors(error) : DeactivatingUserError);
        dispatch(deactivateUserFailure(error));
      });
  };

export function deactivateUserSuccess(userId) {
  return {
    type: DEACTIVATE_USER_SUCCESS,
    payload: {
      userId
    }
  };
}

export function deactivateUserFailure(error, userId?, inList = true) {
  return {
    type: DEACTIVATE_USER_FAILURE,
    payload: {
      userId,
      inList
    }
  };
}

export function reactivateUserRequest(userId, userName, inList = true) {
  return {
    type: REACTIVATE_USER_REQUEST,
    meta: {
      remote: true
    },
    payload: {
      userId,
      inList,
      userName,
      error: ''
    }
  };
}

export const reactivateUser =
  (userId, userName, inList = true) =>
  (dispatch) => {
    dispatch(reactivateUserRequest(userId, userName, inList));
    return apimCall(GoFormzServices.AccountService, `/users/${userId}/reactivate`, 'POST')
      .then(() => {
        analyticsTrack('User Activation Toggled', {
          status: 'Activated'
        });
        dispatch(reactivateUserSuccess(userId));
        showBanner(formatString(SuccessfulUserReactivation, userName));
      })
      .catch((error) => {
        showMessage(handleAxiosErrors(error) !== '' ? handleAxiosErrors(error) : ReactivatingUserError);
        dispatch(reactivateUserFailure(error));
      });
  };

export function reactivateUserSuccess(userId) {
  return {
    type: REACTIVATE_USER_SUCCESS,
    payload: {
      userId
    }
  };
}

export function reactivateUserFailure(error, userId?, inList = true) {
  return {
    type: REACTIVATE_USER_FAILURE,
    payload: {
      userId,
      inList
    }
  };
}

export function reAddGroup(groupId) {
  return {
    type: RE_ADD_GROUP,
    payload: {
      groupId,
      error: ''
    }
  };
}

export function removeCurrentGroup(groupId) {
  return {
    type: REMOVE_CURRENT_GROUP,
    payload: {
      groupId,
      error: ''
    }
  };
}

export function fetchNextUserPage() {
  return {
    meta: {
      remote: true
    },
    type: FETCH_NEXT_USER_PAGE
  };
}

export const fetchNextUsers = () => (dispatch, getState: () => RootState) => {
  const users = getState().userList;
  fetchUsers(users.groupId, users.page + 1, users.pageSize, true, users.sortBy, users.sortDir)(dispatch);
};

export function updateUserInfo(info) {
  return {
    type: UPDATE_USER_INFO,
    payload: info
  };
}

export function updateSelectedGroups(groupIds) {
  return {
    type: UPDATE_SELECTED_GROUPS,
    payload: {
      groupIds
    }
  };
}
